import { useT } from "../../../core/i18n";
// index.tsx
import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
} from "react";
import { Table, Select } from "antd";
import { DatatableProps } from "../../data/interface";

const Datatable = forwardRef((props: DatatableProps, ref) => {
  const { t } = useT();

  const {
    columns,
    dataSource,
    Selection,
    handlePagination,
    paginationConfig,
    loading,
    onRowSelection,
    scroll,
    handleSearch,
    children,
    searchPlaceholder = "Search",
    searchValue,
    getCheckboxProps,
    selectAllAcrossPages = false,
    showSearch = true,
  } = props;

  useImperativeHandle(ref, () => ({
    resetSelection: (data: any) => {
      setSelectedRowKeys(data);
    },
    // you can expose more functions here
  }));

  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [Selections, setSelections] = useState<any>(true);
  const [filteredDataSource, setFilteredDataSource] = useState(Array.isArray(dataSource) ? dataSource : []);

  React.useEffect(() => {
    setFilteredDataSource(Array.isArray(dataSource) ? dataSource : []);
  }, [dataSource]);

  React.useEffect(() => {
    if (searchValue !== undefined) {
      setSearchText(searchValue);
    }
  }, [searchValue]);

  React.useEffect(() => {
    if (props.selectedRowKeys !== undefined) {
      setSelectedRowKeys(props.selectedRowKeys);
    }
  }, [props.selectedRowKeys]);

  const getRecordKey = (record: any, index: number) =>
    record?.key ?? record?.id ?? index;

  const onSelectChange = (newSelectedRowKeys: any[], _selectedRows?: any[], info?: { type?: string }) => {
    let nextSelectedRowKeys = newSelectedRowKeys;

    if (selectAllAcrossPages && info?.type === "all") {
      const selectableKeys = filteredDataSource
        .filter((record: any) => !getCheckboxProps?.(record)?.disabled)
        .map(getRecordKey);
      const currentPage = paginationConfig?.current || 1;
      const pageSize = paginationConfig?.pageSize || 10;
      const currentPageSelectableKeys = filteredDataSource
        .slice((currentPage - 1) * pageSize, currentPage * pageSize)
        .filter((record: any) => !getCheckboxProps?.(record)?.disabled)
        .map(getRecordKey);
      const wasCurrentPageFullySelected = currentPageSelectableKeys.length > 0 &&
        currentPageSelectableKeys.every((key: any) => selectedRowKeys.includes(key));

      nextSelectedRowKeys = wasCurrentPageFullySelected ? [] : selectableKeys;
    }

    setSelectedRowKeys(nextSelectedRowKeys);
    if (onRowSelection) onRowSelection(nextSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => {
      if (getCheckboxProps) {
        return getCheckboxProps(record);
      }
      return {};
    }
  };
  useEffect(() => {
    return setSelections(Selection);
  }, [Selection]);

  return (
    <>
      <div className="table-top-data">
        <div className="row p-2 align-items-center">
          <div className="col-sm-4 col-md-3">
            <div className="dataTables_length d-flex align-items-center">
              <label className="mb-0 d-flex align-items-center" style={{ whiteSpace: 'nowrap', color: '#6f6f6f', fontWeight: 500 }}>
                {t("row_per_page")} :
                <Select
                  className="ms-2"
                  value={paginationConfig?.pageSize || 10}
                  onChange={(value) => {
                    if (handlePagination) {
                      handlePagination({
                        ...paginationConfig,
                        pageSize: value,
                        current: 1
                      });
                    }
                  }}
                  options={[
                    { value: 10, label: "10" },
                    { value: 30, label: "30" },
                    { value: 100, label: "100" },
                  ]}
                  style={{ width: 80 }}
                />
              </label>
            </div>
          </div>
          {children ? (
            showSearch ? <div className="col-sm-8 col-md-9">{children}</div> : null
          ) : showSearch ? (
            <div className="col-sm-8 col-md-9">
              <div
                id="DataTables_Table_0_filter"
                className="dataTables_filter text-end mb-0"
              >
                <label>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    placeholder={searchPlaceholder}
                    aria-controls="DataTables_Table_0"
                    value={searchText}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchText(value);
                      if (handleSearch) {
                        handleSearch(value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setSearchText("");
                        if (handleSearch) {
                          handleSearch("");
                        }
                        e.currentTarget.value = "";
                      }
                    }}
                  />
                </label>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {!Selections ? (
        <Table
          className="table datanew dataTable no-footer"
          rowKey={(record: any, index?: number) => record?.key ?? record?.id ?? index ?? 0}
          columns={columns}
          rowHoverable={false}
          dataSource={filteredDataSource}
          size="middle"
          pagination={{
            ...paginationConfig,
            locale: { items_per_page: "" },
            nextIcon: <i className="ti ti-chevron-right" />,
            prevIcon: <i className="ti ti-chevron-left" />,
            showSizeChanger: false,
            pageSizeOptions: ["10", "30", "100"],
            showTotal: (total, range) =>
              `${t("showing")} ${range[0]} - ${range[1]} ${t("of")} ${total} ${t("entries")}`,
            showLessItems: false,
            showQuickJumper: false,
          }}
          onChange={(pagination: any, filters: any, sorter: any) => {
            if (handlePagination) handlePagination(pagination, filters, sorter);
          }}
          loading={loading}
          scroll={scroll}
        />
      ) : (
        <Table
          className="table datanew dataTable no-footer"
          rowKey={(record: any, index?: number) => record?.key ?? record?.id ?? index ?? 0}
          rowSelection={rowSelection}
          columns={columns}
          rowHoverable={false}
          dataSource={filteredDataSource}
          size="middle"
          pagination={{
            ...paginationConfig,
            locale: { items_per_page: "" },
            nextIcon: <i className="ti ti-chevron-right" />,
            prevIcon: <i className="ti ti-chevron-left" />,
            showSizeChanger: false,
            pageSizeOptions: ["10", "30", "100"],
            showTotal: (total, range) =>
              `${t("showing")} ${range[0]} - ${range[1]} ${t("of")} ${total} ${t("entries")}`,
            showLessItems: false,
            showQuickJumper: false,
          }}
          loading={loading}
          onChange={(pagination: any, filters: any, sorter: any) => {
            if (handlePagination) handlePagination(pagination, filters, sorter);
          }}
          scroll={scroll}
        />
      )}
    </>
  );
});

export default Datatable;
